import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '../ui/Modal';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui';
import { SubHeadline } from '../ui/Typography';

interface AccessInstructionEntry {
  id: string;
  accountName: string;
  storageLocation: string;
  otherLocationDetails?: string;
  mfaEnabled: boolean;
  executorInstructions: string;
}
const Frame138: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, updateTaskProgress } = useSessionPersistence();
  const firstName = (location.state as { firstName?: string })?.firstName || 'Your';

  // UI Visibility State Management 
  const [showAccessModal, setShowAccessModal] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  
  // Data Tracking State 
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  // Form Input State 
  const [accountName, setAccountName] = useState<string>('');
  const [storageLocation, setStorageLocation] = useState<string>('');
  const [otherLocationDetails, setOtherLocationDetails] = useState<string>('');
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);
  const [executorInstructions, setExecutorInstructions] = useState<string>('');

  const [entries, setEntries] = useState<AccessInstructionEntry[]>(() => {
    try {
      return session.taskProgress?.['account-access']?.answers?.entries || [];
    } catch (error) {
      console.error('[Frame138] Error initializing entries:', error);
      return [];
    }
  });

  // Sync component state with global session store
  useEffect(() => {
    const sessionEntries = session.taskProgress?.['account-access']?.answers?.entries;
    if (sessionEntries && JSON.stringify(sessionEntries) !== JSON.stringify(entries)) {
      setEntries(sessionEntries);
    }
  }, [session.taskProgress?.['account-access']?.answers?.entries]);

  const resetForm = (): void => {
    setAccountName('');
    setStorageLocation('');
    setOtherLocationDetails('');
    setMfaEnabled(false);
    setExecutorInstructions('');
    setEditingEntryId(null);
  };

  /**
   * Handles the initialization of the "Add New" flow.
   */
  const handleInitiateAdd = (): void => {
    resetForm();
    setShowAccessModal(true);
  };

  /**
   * Handles the initialization of the "Edit" flow.
   */
  const handleInitiateEdit = (entry: AccessInstructionEntry): void => {
    setEditingEntryId(entry.id);
    setAccountName(entry.accountName);
    setStorageLocation(entry.storageLocation);
    setOtherLocationDetails(entry.otherLocationDetails || '');
    setMfaEnabled(entry.mfaEnabled);
    setExecutorInstructions(entry.executorInstructions);
    setShowAccessModal(true);
  };

  /**
   * Finalizes the deletion process and syncs with persistence.
   */
  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTargetId) return;

    const updatedEntries = entries.filter((e) => e.id !== deleteTargetId);
    setEntries(updatedEntries);
    
    await updateTaskProgress(
      'account-access', 
      { entries: updatedEntries }, 
      updatedEntries.length > 0 ? 'in-progress' : 'not-started'
    );
    
    setShowDeleteConfirm(false);
    setDeleteTargetId(null);
  };

  /**
   * Saves the current access instruction entry to the session.
   */
  const handleSaveAccessInstructions = async (): Promise<void> => {
    if (!accountName || !storageLocation) {
      alert('Please fill in required fields');
      return;
    }

    let updatedEntries: AccessInstructionEntry[];
    if (editingEntryId) {
      updatedEntries = entries.map((e) => 
        e.id === editingEntryId 
          ? { ...e, accountName, storageLocation, otherLocationDetails, mfaEnabled, executorInstructions } 
          : e
      );
    } else {
      updatedEntries = [...entries, { 
        id: Date.now().toString(), 
        accountName, 
        storageLocation, 
        otherLocationDetails, 
        mfaEnabled, 
        executorInstructions 
      }];
    }

    setEntries(updatedEntries);
    await updateTaskProgress('account-access', { entries: updatedEntries }, 'in-progress');
    setShowAccessModal(false);
    resetForm();
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Access is everything." />

        <div className="mt-6">
          <SubHeadline>
            Document where your account access is stored and how your executor can obtain it.
          </SubHeadline>
        </div>

        {/* Callouts */}
        <div className="mt-8 space-y-4">
          <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-md text-sm text-cyan-800">
            <strong>Don’t use a password manager?</strong> That’s okay. You can document where your passwords are stored — such as a secure location, written document, or trusted person — and provide instructions for your executor.
          </div>
         <div className="p-4 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-800">
            <strong>Important:</strong> End of an Era does not store passwords, recovery keys, or login credentials.
          </div>
        </div>
    

        {/* Primary Category Tile */}
       <div className="mt-8">
        <button 
            onClick={handleInitiateAdd}
            className="p-6 w-full max-w-sm rounded-lg border border-neutral-200 bg-white hover:border-cyan-600 transition-all text-left shadow-sm flex flex-row items-center gap-4"
        >

        {/* Icon Container */}
        <div className="w-10 h-10 flex-shrink-0 bg-cyan-50 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-cyan-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>

        {/* Text Content */}
        <div className="flex flex-col">
          <p className="font-semibold text-stone-900">Account Access</p>
          <p className="text-sm text-stone-600">Where your login details are stored and how to access them.</p>
        </div>
      </button>
    </div>
    
        {/* Inventory Display */}
        <div className="mt-12">
          <h3 className="text-xl font-semibold text-stone-800 mb-4">
            Account Access Instructions
          </h3>
          <p className="text-sm text-stone-600 mb-6">
            These instructions help your executor understand where access exists. Passwords are never stored in End of an Era.
          </p>
          
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin">
            {entries.map((entry) => (
              <div 
                key={entry.id} 
                className="bg-[#FDFCFB] rounded-xl border border-neutral-100 p-6 shadow-sm"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 bg-cyan-50 rounded-full flex items-center justify-center text-cyan-800">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-stone-900 leading-tight">
                        {entry.accountName}
                      </p>
                      <div className="mt-1 flex flex-col gap-0.5 text-stone-500 text-[15px]">
                        <p>Storage: {entry.storageLocation}{entry.storageLocation === 'Other' && `: ${entry.otherLocationDetails}`}</p>
                        <p>MFA: {entry.mfaEnabled ? 'Enabled' : 'Disabled'}</p>
                        {entry.executorInstructions && (
                          <p>Executor Access Instructions: {entry.executorInstructions}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleInitiateEdit(entry)} 
                      className="px-4 py-1.5 rounded-md border border-cyan-100 text-cyan-600 font-medium text-sm hover:bg-cyan-50 transition-colors"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => { setDeleteTargetId(entry.id); setShowDeleteConfirm(true); }} 
                      className="px-4 py-1.5 rounded-md border border-red-100 text-red-500 font-medium text-sm hover:bg-red-50 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* Add/Edit Modal */}
        <Modal open={showAccessModal} onClose={() => setShowAccessModal(false)}>
          <h2 className="text-xl font-semibold mb-2">Account Access</h2>
          <p className="text-sm text-stone-600 mb-6">Add instructions for how your executor can access this account.</p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">What account is this for?*</label>
              <input 
                type="text" 
                value={accountName} 
                onChange={(e) => setAccountName(e.target.value)} 
                className="w-full p-2 border rounded" 
                placeholder="e.g. Primary Bank" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Where are the login details stored?*</label>
              <select 
                value={storageLocation} 
                onChange={(e) => setStorageLocation(e.target.value)} 
                className="w-full p-2 border rounded"
              >
                <option value="">Select storage method...</option>
                <option value="Password manager">Password manager</option>
                <option value="Written document">Written document</option>
                <option value="Secure physical location">Secure physical location</option>
                <option value="Trusted person">Trusted person</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {storageLocation === 'Other' && (
              <textarea 
                value={otherLocationDetails} 
                onChange={(e) => setOtherLocationDetails(e.target.value)} 
                className="w-full p-2 border rounded" 
                placeholder="Specify storage method..." 
              />
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Is MFA enabled?</label>
              <p className="text-xs text-stone-500 mb-2">This helps your executor understand any additional verification steps.</p>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input type="radio" checked={mfaEnabled} onChange={() => setMfaEnabled(true)} className="mr-2" /> Yes
                </label>
                <label className="flex items-center">
                  <input type="radio" checked={!mfaEnabled} onChange={() => setMfaEnabled(false)} className="mr-2" /> No
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Executor access instructions</label>
              <textarea 
                value={executorInstructions} 
                onChange={(e) => setExecutorInstructions(e.target.value)} 
                className="w-full p-2 border rounded" 
                rows={4} 
                placeholder="Explain how to access this account, including where credentials are stored, who has access today, or where important materials are kept." 
              />
            </div>

            <div className="p-3 bg-stone-50 border rounded text-xs text-stone-600">
              <strong>Why can’t I save passwords here?</strong> To protect you and your executor, End of an Era does not store passwords or recovery keys. Instead, we help you document where access exists and how to retrieve it safely.
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowAccessModal(false)} className="px-4 py-2">Cancel</button>
            <button onClick={handleSaveAccessInstructions} className="px-4 py-2 bg-cyan-700 text-white rounded">Save Access Instructions</button>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <h2 className="text-lg font-semibold mb-4">Are you sure?</h2>
          <p className="text-stone-600 mb-6">This will remove this instruction from your planner.</p>
          <button onClick={handleConfirmDelete} className="w-full p-2 bg-red-600 text-white rounded">Delete Entry</button>
        </Modal>

      </ResponsiveContent>

      {/* Standardized Navigation Footer */}
      <ResponsiveFooter 
        title="Setup Your Plan" 
        subtitle="Documents how your accounts can be accessed" 
        progressPercent={75} 
        onNext={() => navigate('/frame-135')} 
        onBack={() => navigate('/frame-137', { state: { firstName } })} 
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame138;